import { type SystemRole, type UserTier } from '@diario/common';
import { type HTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Rol/color del badge (contrato `components/badges/Badge.d.ts` de la entrega). */
export type BadgeVariant = 'member' | 'grumete' | 'partner' | 'pendiente' | 'accent' | 'neutral' | 'official';

/** Alias de compatibilidad — ver el JSDoc del prop `variant` en `BadgeProps` para el porqué del renombre. */
export type BadgeRole = BadgeVariant;

/**
 * `.d.ts` de origen declara `'sm' | 'md' | 'lg'`; esta tarea (T-2.5) solo pide `sm`/`md` — ningún
 * mock de las iteraciones 01–06 usa `lg`, así que no se porta (regla anti-cajón-de-sastre,
 * `AGENTS.md`: no se añade lo que ningún consumidor real pide todavía).
 */
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	/** Punto de color a la izquierda (decorativo: el nombre accesible lo da `children`). */
	dot?: boolean;
	/** Emoji, nodo, o URL de imagen (icono de marca) — ver `iconoDeRol`. */
	icon?: ReactNode | string;
	/** `alt` del icono cuando `icon` es una URL. Vacío por defecto (decorativo, el texto del badge ya lo dice). */
	iconAlt?: string;
	size?: BadgeSize;
	/** Relleno fuerte en vez de tinte suave (10–24 %). */
	solid?: boolean;
	/**
	 * Color/variante del badge. Corresponde al prop `role` de `Badge.d.ts` (contrato de origen,
	 * `components/badges/Badge.d.ts`); se renombra a `variant` porque colisiona con el atributo
	 * ARIA `role` y con la regla `useValidAriaRole` de Biome — `HTMLAttributes<HTMLSpanElement>`
	 * ya declara `role?: AriaRole | undefined` (vía `AriaAttributes`), y esa regla marca cualquier
	 * atributo JSX llamado `role` escrito como literal de cadena que no sea un rol ARIA válido, sin
	 * distinguir el prop propio de `Badge` del atributo nativo (`biome.json` es fichero congelado,
	 * no se puede ajustar la regla desde esta tarea). Al no redeclararlo aquí, el atributo ARIA
	 * `role` nativo vuelve a estar disponible en `...rest` (p. ej. `<Badge role='status'>`).
	 */
	variant?: BadgeVariant;
}

/**
 * Paleta por rol: tinte suave (por defecto, texto legible del color oscuro correspondiente) y
 * relleno sólido (`solid`). El punto (`dot`) usa el color crudo del rol en modo tinte, y
 * `currentColor` en modo sólido (así queda visible sobre el propio relleno).
 *
 * Ningún texto usa un color de rol crudo como `color` (regla del design system, §Contraste):
 * `member`/`partner` no tienen token `-ink` propio en `tokens.css`, así que su texto va en
 * `text-ink`, igual que exige la regla para `pendiente`/`neutral`. `grumete` → `grumete-ink`,
 * `accent` → `accent-ink` (los dos casos que sí cita la tarea, con token dedicado).
 * `pendiente`/`official` no tienen tinte propio en `tokens.css` (solo existen `member-tint`,
 * `grumete-tint`, `partner-tint`, `accent-tint`): se derivan con el modificador de opacidad de
 * Tailwind (`bg-pendiente/14`, `bg-stamp-oficial/16`), mismo patrón que ya usa el repositorio
 * (`bg-role-grumete/90`, `bg-accent/10` en `tarjetaCompartir.tsx`/`formularioAcceso.tsx`).
 *
 * Nota de contraste conocida sobre `solid` + `member`: `--da-member` (#dc00d3) tiene una
 * luminancia intermedia que no llega a 4,5:1 ni con texto blanco (~4,21:1) ni con `text-ink`
 * (~3,64:1) — el propio `tokens.css` no ofrece un tono más oscuro para ese caso (a diferencia de
 * `grumete`/`gray`, que sí lo tienen). Se usa `text-text-on-dark` por ser la opción más cercana a AA y
 * la que ya usaba el `Badge.jsx` de referencia; corregirlo de raíz exige un token nuevo
 * (`--da-member-ink` o similar), fuera del alcance de esta tarea (fichero `tokens.css` no está en
 * la lista de ficheros permitidos).
 */
const ROLE_CLASSES: Record<
	BadgeVariant,
	{ dotTint: string; solidBg: string; solidText: string; tintBg: string; tintText: string }
> = {
	accent: {
		dotTint: 'bg-accent',
		solidBg: 'bg-accent',
		solidText: 'text-accent-ink',
		tintBg: 'bg-accent-tint',
		tintText: 'text-accent-ink',
	},
	grumete: {
		dotTint: 'bg-grumete',
		solidBg: 'bg-grumete',
		solidText: 'text-ink-warm',
		tintBg: 'bg-grumete-tint',
		/**
		 * `font-bold`: el propio comentario de `tokens.css` sobre `--da-grumete-ink` ya anticipa
		 * este caso («pastilla #e9e9e9 4,28:1 — ahí va en negrita»). El tinte mide 4,37:1 (H-19),
		 * 0,13 por debajo del umbral AA de 4,5:1; WCAG 1.4.3 no rebaja el umbral por negrita salvo
		 * en texto grande (≥18,66 px en negrita), así que esto no basta por sí solo para alcanzar
		 * AA — sigue reportado como pregunta abierta para el cliente (¿ajustar el tinte en vez de
		 * la tipografía?), no se toca ningún token desde esta tarea.
		 */
		tintText: 'text-grumete-ink font-bold',
	},
	member: {
		dotTint: 'bg-member',
		solidBg: 'bg-member',
		solidText: 'text-text-on-dark',
		tintBg: 'bg-member-tint',
		tintText: 'text-ink',
	},
	neutral: {
		dotTint: 'bg-gray',
		solidBg: 'bg-ink',
		solidText: 'text-text-on-dark',
		tintBg: 'bg-paper-2',
		tintText: 'text-gray-ink',
	},
	official: {
		dotTint: 'bg-stamp-oficial',
		solidBg: 'bg-stamp-oficial',
		solidText: 'text-ink',
		tintBg: 'bg-stamp-oficial/16',
		tintText: 'text-ink',
	},
	partner: {
		dotTint: 'bg-partner',
		solidBg: 'bg-partner',
		solidText: 'text-ink',
		tintBg: 'bg-partner-tint',
		tintText: 'text-ink',
	},
	pendiente: {
		dotTint: 'bg-pendiente',
		solidBg: 'bg-pendiente',
		solidText: 'text-ink',
		tintBg: 'bg-pendiente/14',
		tintText: 'text-gray-ink',
	},
};

/** Altura mínima 24 px (`min-h-6`) en ambos tamaños: no es un control pulsable, pero mantiene el ritmo vertical del sistema. */
const SIZE_CLASSES: Record<BadgeSize, string> = {
	md: 'gap-1.5 px-3 py-1 text-sm normal-case',
	sm: 'gap-1 px-2 py-0.5 text-xs uppercase tracking-label',
};

/**
 * Badge de rol o estado. Pastilla (`rounded-pill`) con tinte suave del color de rol (uso
 * puntual, nunca rellenos grandes); `solid` para destacar. Cero texto propio: la etiqueta entra
 * por `children`.
 *
 * Contrato de origen: `components/badges/Badge.d.ts` de la entrega del 2026-09-02 (el `.jsx` no
 * se porta, ver ADR-0021 y `docs/design-system.md` § Primitivos) — mismos nombres de prop salvo
 * `role` → `variant` (ver JSDoc del prop en `BadgeProps`), `size` reducido a `sm`/`md` (ver `BadgeSize`).
 */
export const Badge = ({
	children,
	className = '',
	dot = false,
	icon = null,
	iconAlt = '',
	size = 'md',
	solid = false,
	variant = 'neutral',
	...rest
}: BadgeProps): React.JSX.Element => {
	const paleta = ROLE_CLASSES[variant];
	const claseFondo = solid ? paleta.solidBg : paleta.tintBg;
	const claseTexto = solid ? paleta.solidText : paleta.tintText;
	const claseDot = solid ? 'bg-current' : paleta.dotTint;
	/**
	 * `font-medium` va aquí, no fijo en la clase base: `claseTexto` de `grumete` en tinte incluye
	 * `font-bold` (H-19), y Tailwind 4 emite `.font-bold` ANTES que `.font-medium` en la hoja de
	 * estilos final (mismo `@layer utilities`, igual especificidad) — comprobado inspeccionando
	 * `document.styleSheets` en la story `ui-badge--roles`. Con las dos clases en el mismo `<span>`,
	 * la que aparece más tarde en la hoja gana la cascada, así que un `font-medium` fijo en la base
	 * anulaba en silencio el `font-bold` del tinte (el texto seguía pintándose en peso 500). Un
	 * peso por defecto condicional, en vez de un `font-medium` incondicional, evita que ambas
	 * clases convivan nunca en el mismo elemento.
	 */
	const clasePeso = claseTexto.includes('font-bold') ? '' : 'font-medium';

	return (
		<span
			className={`inline-flex min-h-6 items-center whitespace-nowrap rounded-pill font-body ${clasePeso} leading-none ${SIZE_CLASSES[size]} ${claseFondo} ${claseTexto} ${className}`}
			{...rest}
		>
			{dot ? <span aria-hidden='true' className={`h-[7px] w-[7px] shrink-0 rounded-pill ${claseDot}`} /> : null}
			{renderizarIcono(icon, iconAlt)}
			{children}
		</span>
	);
};

/**
 * `icon` acepta emoji, nodo o URL de imagen (`Badge.d.ts`): una cadena que empieza por `/` o
 * `http` se pinta como `<img>` de 16 px (`h-4 w-4`, `alt` de `iconAlt`); cualquier otra cosa
 * (emoji, `ReactNode`, el glifo `⬡`) se pinta dentro de un `<span aria-hidden='true'>` propio —
 * decorativo, igual que el `dot`, porque el nombre accesible del badge ya lo da `children` — y
 * así queda en su propio nodo de texto en vez de fundirse con el de `children` (el mismo motivo
 * por el que `tablonParticipante.tsx`/`sendaRoles.tsx` envuelven sus iconos de texto). Función
 * aparte (en vez de una bandera booleana calculada antes del JSX) para que TypeScript estreche
 * `icon` a `string` dentro del propio `if`, sin `as string`.
 */
function renderizarIcono(icon: BadgeProps['icon'], iconAlt: string): ReactNode {
	if (typeof icon === 'string' && (icon.startsWith('/') || icon.startsWith('http'))) {
		return <img alt={iconAlt} className='h-4 w-4 shrink-0 object-contain' src={icon} />;
	}
	if (icon == null) {
		return null;
	}
	return (
		<span aria-hidden='true' className='shrink-0'>
			{icon}
		</span>
	);
}

/** Icono de marca por rol, en el orden en que aparece en `public/brand/`. */
const ICONOS_DE_ROL: Partial<Record<BadgeVariant, string>> = {
	grumete: '/brand/grumete.png',
	member: '/brand/miembro.png',
	official: '/brand/experiencia_oficial.png',
	pendiente: '/brand/pendiente.png',
};

/**
 * Icono de marca de un rol (regla de iconografía, `docs/design-system.md` § Iconografía: para
 * roles y sellos se usa el asset vendorizado de `public/brand/`, nunca un emoji).
 *
 * Excepción decidida por el cliente el 31/08 (`.../2026-09-02-fundacion/guidelines/
 * marca-la-seta.card.html`, brandbook §3.5): el colaborador (`partner`) es el glifo `⬡`
 * (festoneado teal `--da-partner`), **no** `colaborador.png` — ese fichero del catálogo público
 * sigue pintando una seta por un cabo suelto del cliente todavía sin resolver, y una seta
 * significa "Tuad", no "colaborador".
 *
 * `accent`/`neutral` no tienen sello de marca: no son peldaños de la escalera de tiers de la
 * comunidad, así que devuelven `undefined` (sin icono).
 */
export function iconoDeRol(variant: BadgeVariant): string | undefined {
	if (variant === 'partner') {
		return '⬡';
	}
	return ICONOS_DE_ROL[variant];
}

const ROL_POR_TIER: Record<UserTier, BadgeVariant> = {
	COLLABORATOR: 'partner',
	GRUMETE: 'grumete',
	LEGACY: 'member',
	LITE: 'member',
	PENDING: 'pendiente',
	STANDARD: 'member',
};

/**
 * Deriva el `variant` visual del badge a partir del tier de comunidad (`UserTier` de
 * `@diario/common`, documento 02a §5.3): `STANDARD`/`LITE`/`LEGACY` → `member` (los tres tiers de
 * pago de la escalera comparten el mismo color de marca), `GRUMETE` → `grumete`, `COLLABORATOR`
 * → `partner`, `PENDING` → `pendiente`.
 *
 * `systemRole === 'ADMIN'` gana siempre y devuelve `'neutral'`: administración es un permiso de
 * sistema (`SYSTEM_ROLES` en `@diario/common`), no un peldaño de la escalera de tiers, así que no
 * tiene color de marca propio en este badge — un admin sigue teniendo un `tier` de comunidad
 * (normalmente `STANDARD`), pero mostrarlo en magenta de "miembro" mezclaría dos escalas
 * distintas (permiso de sistema vs. tier de comunidad) en un solo color.
 */
export function roleDesdeTier(tier: UserTier, systemRole?: SystemRole): BadgeVariant {
	if (systemRole === 'ADMIN') {
		return 'neutral';
	}
	return ROL_POR_TIER[tier];
}

export interface SelloViejaEscuelaProps {
	/** Clase adicional para el contenedor — solo para separación en el sitio donde se use. */
	className?: string;
}

/** Único glifo del sello (P-68): el brandbook no vendoriza ningún asset para "vieja escuela" —
 * a diferencia de los cuatro roles de `ICONOS_DE_ROL`, aquí no hay `.png` que pintar —, así que
 * se usa el mismo glifo con el que el propio cliente nombra la idea (`respuestas.md`, P-68: «El
 * sello ⚓ de vieja escuela»), igual que `iconoDeRol('partner')` ya usa `⬡` cuando el asset
 * vendorizado no sirve. */
const EMOJI_VIEJA_ESCUELA = '⚓';

/**
 * El sello ⚓ de la comunidad histórica (P-68, `isOldSchool` de `@diario/common`): distingue a
 * quien ya estaba antes de la plataforma. Deliberadamente NO es un `BadgeVariant` octavo ni una
 * prop de `Badge`, por dos motivos:
 *
 * 1. **Acompaña a la persona, no a lo que un `Badge` concreto esté etiquetando.** En
 *    `tablonParticipante.tsx` el único `Badge` de cada asistente ya lleva su subrole elegido
 *    (p. ej. «Cocinillas») y ni siquiera se pinta si la persona no lo tiene — colgar el ancla de
 *    ESE badge la ataría a un dato sin relación (subrole ≠ antigüedad) y la haría desaparecer
 *    justo para quien no eligió subrole, aunque sí sea de vieja escuela.
 * 2. **`Badge` no tiene texto propio** («Cero texto propio: la etiqueta entra por children»,
 *    JSDoc de más arriba) y el resto de sus usos en el repositorio no tienen nada que ver con
 *    esto: hornear aquí una traducción fija de un solo caso, dentro de una pieza que consume
 *    toda la aplicación, sería el "cajón de sastre" que `AGENTS.md` prohíbe.
 *
 * Por eso es un componente hermano, exportado desde el mismo fichero que `Badge` (no uno nuevo:
 * fuera de la lista de ficheros permitidos de esta tarea) en vez de vivir en su propio módulo.
 * Sin color de rol ni fondo — `ROLE_CLASSES` no interviene aquí, porque quien lo lleva sigue
 * siendo el tier/color que ya tenía (`roleDesdeTier` no cambia).
 *
 * Icono-only con nombre accesible propio (`role='img'` + `aria-label`): misma receta que
 * `SelloRol` (`features/shell/components/selloRol.tsx`) usa para el mismo problema — un emoji
 * suelto no dice nada a un lector de pantalla, así que el nombre accesible no depende del emoji.
 */
export const SelloViejaEscuela = ({ className = '' }: SelloViejaEscuelaProps): React.JSX.Element => {
	const { t } = useTranslation('common');

	return (
		<span aria-label={t('selloViejaEscuela')} className={`inline-flex items-center ${className}`} role='img'>
			<span aria-hidden='true'>{EMOJI_VIEJA_ESCUELA}</span>
		</span>
	);
};
